import React, { useState, useEffect } from 'react';
import { Button } from '@/components/shadcn-ui/button';

const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleError = errorEvent => {
      console.error('Error captured by boundary:', errorEvent.error, errorEvent);

      setHasError(true);
      setErrorMessage(errorEvent.error?.message || 'An unexpected error occurred');

      // Prevent the error from propagating further
      errorEvent.preventDefault();
    };

    const handleUnhandledRejection = rejectionEvent => {
      console.error('Unhandled promise rejection captured:', rejectionEvent.reason);

      setHasError(true);
      setErrorMessage(rejectionEvent.reason?.message || 'An unexpected error occurred');
    };

    // Add global error listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const retry = () => {
    setHasError(false);
    setErrorMessage('');
  };

  if (hasError) {
    return (
      <div className="fixed inset-0 grid place-content-center bg-black/80 backdrop-blur-sm z-[9999]">
        <div className="bg-background rounded-lg shadow-2xl p-8 w-[90%] max-w-md text-center border-2 border-destructive relative">
          <h3 className="mb-4 text-2xl font-semibold text-destructive flex items-center justify-center gap-2">
            <span role="img" aria-label="warning">⚠️</span> Something went wrong
          </h3>
          <p className="mb-6 text-base leading-relaxed text-foreground bg-muted p-4 rounded border-l-4 border-destructive text-left font-mono text-sm break-words">
            {errorMessage}
          </p>
          <Button
            onClick={retry}
            className="bg-gradient-to-br from-primary to-blue-600 hover:translate-y-[-2px] transition-transform shadow-lg"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return children;
};

export default ErrorBoundary;
